import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type SamlMetadataResult, type SsoSettingsResult } from '@whinerp/client';
import { Button, ErrorState, Field, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';

/**
 * Setup > Single sign-on (F-003).
 *
 * The screen's job is to make two invisible things visible: whether anybody
 * has ever actually signed in through this connection, and whether enforcing
 * it would strand the workspace. Both are refusals on the server; showing them
 * here is what stops an administrator filling the form in, saving it, and
 * reasonably believing their people can sign in.
 */

interface Role {
  roleId: string;
  name: string;
}

export function SsoPanel({ roles }: { roles: readonly Role[] }): JSX.Element {
  const t = useT();
  const errorMessage = useErrorMessage();
  const queryClient = useQueryClient();

  const data = useQuery<SsoSettingsResult, ApiError>({
    queryKey: ['sso-settings'],
    queryFn: () => api.ssoSettings({}),
  });

  const [protocol, setProtocol] = useState<'oidc' | 'saml'>('oidc');
  const [issuer, setIssuer] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [authorizeUrl, setAuthorizeUrl] = useState('');
  const [tokenUrl, setTokenUrl] = useState('');
  const [jwksUrl, setJwksUrl] = useState('');
  const [entityId, setEntityId] = useState('');
  const [ssoUrl, setSsoUrl] = useState('');
  const [certificate, setCertificate] = useState('');
  const [domains, setDomains] = useState('');
  const [jitProvisioning, setJitProvisioning] = useState(false);
  const [jitRoleId, setJitRoleId] = useState('');
  const [label, setLabel] = useState('');
  /*
   * How long a new token lasts. Null means it does not expire.
   *
   * The default is "does not expire", which is what every token issued before
   * this control existed was promised - and changing the default would quietly
   * put an end date on the next token an administrator issues without them
   * choosing one.
   */
  const [expiresInDays, setExpiresInDays] = useState<number | null>(null);
  const [issued, setIssued] = useState<string | null>(null);

  const connection = data.data?.connection ?? null;

  /*
   * The form starts from what is saved. Only when the answer arrives, and only
   * once - re-seeding on every render would overwrite what somebody is typing
   * the moment any query refetched.
   */
  useEffect(() => {
    if (!connection) return;
    setProtocol(connection.protocol);
    setIssuer(connection.issuer ?? '');
    setClientId(connection.clientId ?? '');
    setAuthorizeUrl(connection.authorizeUrl ?? '');
    setTokenUrl(connection.tokenUrl ?? '');
    setJwksUrl(connection.jwksUrl ?? '');
    setEntityId(connection.entityId ?? '');
    setSsoUrl(connection.ssoUrl ?? '');
    setCertificate(connection.certificate ?? '');
    setDomains(connection.domains.join(', '));
    setJitProvisioning(connection.jitProvisioning);
    setJitRoleId(connection.jitRoleId ?? '');
  }, [connection]);

  const save = useMutation({
    mutationFn: () =>
      api.saveSsoSettings({
        body: {
          protocol,
          issuer,
          clientId,
          // Blank leaves the stored secret alone. A form that cannot show a
          // secret must not erase it by being saved.
          clientSecret,
          authorizeUrl,
          tokenUrl,
          jwksUrl,
          entityId,
          ssoUrl,
          certificate,
          domains: domains
            .split(',')
            .map((d) => d.trim())
            .filter((d) => d !== ''),
          jitProvisioning,
          jitRoleId: jitRoleId === '' ? null : jitRoleId,
        },
      }),
    onSuccess: async () => {
      setClientSecret('');
      await queryClient.invalidateQueries({ queryKey: ['sso-settings'] });
    },
  });

  const enforce = useMutation({
    mutationFn: (enforced: boolean) => api.setSsoEnforcement({ body: { enforced } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sso-settings'] });
    },
  });

  const issueToken = useMutation({
    mutationFn: () =>
      api.createScimToken({
        body: { label, ...(expiresInDays === null ? {} : { expiresInDays }) },
      }),
    onSuccess: async (result) => {
      setIssued(result.token);
      setLabel('');
      await queryClient.invalidateQueries({ queryKey: ['sso-settings'] });
    },
  });

  const revokeToken = useMutation({
    mutationFn: (tokenId: string) => api.revokeScimToken({ path: { tokenId } }),
    onSuccess: async () => {
      setIssued(null);
      await queryClient.invalidateQueries({ queryKey: ['sso-settings'] });
    },
  });

  if (data.isPending) return <Skeleton className="h-block-md w-full" />;
  if (data.isError) {
    return (
      <ErrorState title={t('state.error')} body={data.error.message} onRetry={() => void data.refetch()} />
    );
  }

  return (
    <Panel data-region="sso" id="sso">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('sso.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('sso.body')}</p>

        {/*
          Whether anybody has ever signed in through this connection. The
          server refuses enforcement without it, so an administrator who cannot
          see it has no way to find out why the switch will not move.
        */}
        <p
          data-sso-verified={connection?.verifiedAt === null || connection === null ? 'no' : 'yes'}
          className={
            connection?.verifiedAt
              ? 'text-dense text-ink-600'
              : 'text-dense text-warning-text'
          }
        >
          {connection?.verifiedAt
            ? t('sso.verified', { values: { when: connection.verifiedAt.slice(0, 10) } })
            : t('sso.notVerified')}
        </p>

        <label className="flex items-center gap-2 text-dense text-ink-600">
          {t('sso.protocol')}
          <select
            data-sso-protocol
            value={protocol}
            onChange={(event) => setProtocol(event.currentTarget.value as 'oidc' | 'saml')}
            className="h-input rounded-control border border-control bg-surface px-2 text-dense text-ink"
          >
            <option value="oidc">OIDC</option>
            <option value="saml">SAML 2.0</option>
          </select>
        </label>

        {protocol === 'oidc' ? (
          <div className="flex flex-col gap-2">
            <Field label={t('sso.issuer')} data-sso-issuer value={issuer} onChange={(e) => setIssuer(e.currentTarget.value)} />
            <Field label={t('sso.clientId')} data-sso-client-id value={clientId} onChange={(e) => setClientId(e.currentTarget.value)} />
            <Field
              label={t('sso.clientSecret')}
              data-sso-client-secret
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.currentTarget.value)}
              hint={connection?.hasClientSecret ? t('sso.secretKept') : undefined}
            />
            <Field label={t('sso.authorizeUrl')} data-sso-authorize-url value={authorizeUrl} onChange={(e) => setAuthorizeUrl(e.currentTarget.value)} />
            <Field label={t('sso.tokenUrl')} data-sso-token-url value={tokenUrl} onChange={(e) => setTokenUrl(e.currentTarget.value)} />
            <Field label={t('sso.jwksUrl')} data-sso-jwks-url value={jwksUrl} onChange={(e) => setJwksUrl(e.currentTarget.value)} />
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {/*
              The half of "metadata exchange" that is ours to hand over.

              Configuring SAML by hand is retyping four values into a console,
              each one a chance to paste a trailing space into a URL and meet
              an error that mentions a signature and not the space. These come
              from the server, which computes them from the one URL the
              verifier itself insists on - so a value shown here that the
              callback would refuse is not expressible.
            */}
            <SamlMetadata />
            <Field label={t('sso.entityId')} data-sso-entity-id value={entityId} onChange={(e) => setEntityId(e.currentTarget.value)} />
            <Field label={t('sso.ssoUrl')} data-sso-sso-url value={ssoUrl} onChange={(e) => setSsoUrl(e.currentTarget.value)} />
            <Field label={t('sso.certificate')} data-sso-certificate value={certificate} onChange={(e) => setCertificate(e.currentTarget.value)} />
          </div>
        )}

        <Field
          label={t('sso.domains')}
          data-sso-domains
          value={domains}
          hint={t('sso.domains.help')}
          onChange={(e) => setDomains(e.currentTarget.value)}
        />

        <label className="flex items-baseline gap-2 text-body text-ink">
          <input
            type="checkbox"
            data-sso-jit
            checked={jitProvisioning}
            onChange={(event) => setJitProvisioning(event.currentTarget.checked)}
          />
          {t('sso.jit')}
        </label>
        <p className="text-meta text-ink-meta">{t('sso.jit.help')}</p>
        {jitProvisioning ? (
          <label className="flex items-center gap-2 text-dense text-ink-600">
            {t('sso.jit.role')}
            <select
              data-sso-jit-role
              value={jitRoleId}
              onChange={(event) => setJitRoleId(event.currentTarget.value)}
              className="h-input rounded-control border border-control bg-surface px-2 text-dense text-ink"
            >
              <option value="">—</option>
              {roles.map((role) => (
                <option key={role.roleId} value={role.roleId}>
                  {role.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <div>
          <Button intent="secondary" size="small" data-sso-save busy={save.isPending} onClick={() => save.mutate()}>
            {t('sso.save')}
          </Button>
        </div>
        {save.isError ? (
          <p data-sso-refusal className="text-dense text-danger-text">
            {errorMessage(save.error)}
          </p>
        ) : null}

        {/* Enforcement, and the three refusals that stand in front of it. */}
        <label className="flex items-baseline gap-2 text-body text-ink">
          <input
            type="checkbox"
            data-sso-enforce
            checked={connection?.enforced ?? false}
            disabled={connection === null || enforce.isPending}
            onChange={(event) => enforce.mutate(event.currentTarget.checked)}
          />
          {t('sso.enforce')}
        </label>
        <p className="text-meta text-ink-meta">
          {connection?.enforced ? t('sso.enforced') : t('sso.notEnforced')}
        </p>
        {enforce.isError ? (
          <p data-sso-enforce-refusal className="text-dense text-danger-text">
            {errorMessage(enforce.error)}
          </p>
        ) : null}

        {/* Directory provisioning. */}
        <SectionLabel>{t('sso.scim.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('sso.scim.body')}</p>
        <div className="flex flex-wrap items-end gap-2">
          <Field label={t('sso.scim.label')} data-scim-label value={label} onChange={(e) => setLabel(e.currentTarget.value)} />
          <label className="flex flex-col gap-1">
            <span className="text-micro uppercase tracking-label text-ink-label">
              {t('sso.scim.expiry')}
            </span>
            <select
              data-scim-expiry
              value={expiresInDays === null ? '' : String(expiresInDays)}
              onChange={(event) =>
                setExpiresInDays(event.target.value === '' ? null : Number(event.target.value))
              }
              className="h-input rounded-control border border-control bg-surface px-2 text-body text-ink"
            >
              <option value="">{t('sso.scim.expiry.never')}</option>
              {[30, 90, 365].map((days) => (
                <option key={days} value={days}>
                  {t('sso.scim.expiry.days', { values: { count: String(days) } })}
                </option>
              ))}
            </select>
          </label>
          <Button intent="secondary" size="small" data-scim-create busy={issueToken.isPending} onClick={() => issueToken.mutate()}>
            {t('sso.scim.create')}
          </Button>
        </div>
        {issued ? (
          <div data-scim-secret className="flex flex-col gap-1 rounded-control bg-sunken p-2">
            <code className="break-all font-mono text-dense text-ink">{issued}</code>
            <p className="text-meta text-ink-meta">{t('sso.scim.once')}</p>
          </div>
        ) : null}
        {issueToken.isError ? (
          <p data-scim-refusal className="text-dense text-danger-text">
            {errorMessage(issueToken.error)}
          </p>
        ) : null}

        {data.data.scimTokens.length === 0 ? (
          <p className="text-dense text-ink-meta">{t('sso.scim.none')}</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {data.data.scimTokens.map((token) => (
              <li key={token.tokenId} data-scim-token={token.tokenId} className="flex items-center gap-2 text-dense text-ink">
                <span>{token.label === '' ? token.tokenId.slice(0, 8) : token.label}</span>
                <span className="text-ink-meta">
                  {token.lastUsedAt
                    ? t('sso.scim.lastUsed', { values: { when: token.lastUsedAt.slice(0, 10) } })
                    : t('sso.scim.neverUsed')}
                </span>
                {/*
                  When it runs out, on the row.

                  A token that expires and nothing says when is an outage that
                  arrives on a Tuesday with no explanation on any screen - and
                  this is the screen somebody would look at. `expired` comes
                  from the server, because it is the database's clock that the
                  refusal compares against.
                */}
                {token.expired ? (
                  <span data-scim-expired={token.tokenId} className="text-danger-text">
                    {t('sso.scim.expired')}
                  </span>
                ) : token.expiresAt ? (
                  <span data-scim-expires={token.tokenId} className="text-ink-meta">
                    {t('sso.scim.expiresOn', { values: { when: token.expiresAt.slice(0, 10) } })}
                  </span>
                ) : null}
                <Button
                  intent="ghost"
                  size="small"
                  data-scim-revoke={token.tokenId}
                  onClick={() => revokeToken.mutate(token.tokenId)}
                >
                  {t('sso.scim.revoke')}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}

/**
 * The two values a SAML provider has to be told, and the document holding them.
 *
 * Read from the server rather than composed here. The entity id is derived
 * from the reply URL, the reply URL is where this deployment actually listens,
 * and the verifier compares the assertion's audience against the same pair -
 * so what is shown on this screen and what the callback will accept cannot
 * drift apart.
 *
 * The signing note is here because it is the one setting an administrator will
 * otherwise get wrong and be unable to diagnose: this deployment requires the
 * ASSERTION to be signed, not just the response around it, and a provider
 * configured the other way fails with a message about a signature that does
 * not say which one.
 */
function SamlMetadata(): JSX.Element | null {
  const t = useT();
  const metadata = useQuery<SamlMetadataResult, ApiError>({
    queryKey: ['saml-metadata'],
    queryFn: () => api.samlMetadata({}),
  });

  if (metadata.isPending) return <Skeleton className="h-block-sm w-full" />;
  /*
   * Silent on failure, deliberately.
   *
   * This is a convenience beside the fields, not the way the connection is
   * configured. An error banner here would sit on top of the form somebody
   * came to fill in, about something that does not stop them filling it in.
   */
  if (metadata.isError) return null;

  return (
    <div data-saml-metadata className="flex flex-col gap-1 rounded-control bg-sunken p-3">
      <p className="text-micro uppercase tracking-label text-ink-label">
        {t('sso.saml.metadata')}
      </p>
      <dl className="flex flex-col gap-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <dt className="text-meta text-ink-meta">{t('sso.saml.entityId')}</dt>
          {/* Selectable, and wrapping: these are long and get pasted. */}
          <dd data-saml-entity-id className="min-w-0 select-all break-all text-dense text-ink">
            {metadata.data.entityId}
          </dd>
        </div>
        <div className="flex flex-wrap items-baseline gap-2">
          <dt className="text-meta text-ink-meta">{t('sso.saml.acs')}</dt>
          <dd data-saml-acs className="min-w-0 select-all break-all text-dense text-ink">
            {metadata.data.callbackUrl}
          </dd>
        </div>
      </dl>
      <p className="text-meta text-ink-meta">{t('sso.saml.signing')}</p>
    </div>
  );
}
